import { 
  ChartPie as ChartPieIcon,
  GearSix as GearSixIcon,
  PlugsConnected as PlugsConnectedIcon,
  User as UserIcon,
  Users as UsersIcon,
  XSquare,
  CreditCard as CreditCardIcon,
  SquaresFour as SquaresFourIcon,
  LockKey as LockKeyIcon,
  UsersThree as UsersThreeIcon,
  ToggleRight as ToggleRightIcon,
  UsersFour as UsersFourIcon,
  ShieldCheck as ShieldCheckIcon,
  SlidersHorizontal as SlidersHorizontalIcon,
  AddressBookTabs as AddressBookTabsIcon,
  Storefront as StorefrontIcon,
  TrendUp as TrendUpIcon,
  Package as PackageIcon,
  ShoppingCart as ShoppingCartIcon,
  Wallet as WalletIcon,
  Gauge as GaugeIcon,
  Receipt as ReceiptIcon,
  Megaphone as MegaphoneIcon,
  Scales as ScalesIcon,
  ShareNetwork as ShareNetworkIcon,
  ChartLineUp as ChartLineUpIcon,
} from "@phosphor-icons/react/dist/ssr";
import * as React from "react";
import { getLoadedIconLibrary, libraryIcon, useIconLibrary, kebabToPascal } from "./iconLibrary";

// Icons bundled up front (instant) — includes the legacy alias names
// (orders, sellvia, bill, …) older modules were saved with.
export const staticNavIcons = {
  "chart-pie": ChartPieIcon,
  "gear-six": GearSixIcon,
  "plugs-connected": PlugsConnectedIcon,
  "x-square": XSquare,
  user: UserIcon,
  users: UsersIcon,
  subscription: CreditCardIcon,

  // Master Settings icons
  "squares-four": SquaresFourIcon,
  "lock-key": LockKeyIcon,
  "users-three": UsersThreeIcon,
  "toggle-right": ToggleRightIcon,
  "users-four": UsersFourIcon,
  "role-permission": ShieldCheckIcon,
  "module-permission": SlidersHorizontalIcon,
  "role": AddressBookTabsIcon,
  "sellvia": GaugeIcon,
  "store": StorefrontIcon,
  "orders": ShoppingCartIcon,
  "products": PackageIcon,
  "financials": TrendUpIcon,
  "payout": WalletIcon,
  "bill": ReceiptIcon,
  "adds": MegaphoneIcon,
  "balances": ScalesIcon,
  "social-automation": ShareNetworkIcon,
  // "MarketSync Dashboard" sub-module — bundled so the sidebar never has to
  // download the full icon library just to draw it.
  "chart-line-up": ChartLineUpIcon,
};

// ── Any other Phosphor icon by its kebab-case name ──────────────────────
// Modules can now pick ANY icon of the library (icon picker on the Module
// page). Those aren't imported statically, so a lookup for one returns a
// small wrapper that renders the real icon as soon as the full library
// chunk has loaded (see iconLibrary.jsx). One wrapper per name, cached, so
// React never remounts it.
const lazyIconCache = new Map();
const ICON_NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const lazyIcon = (name) => {
  if (!lazyIconCache.has(name)) {
    const LazyIcon = React.forwardRef(function LazyIcon(props, ref) {
      useIconLibrary();
      const Real = libraryIcon(name);
      if (Real) return <Real ref={ref} {...props} />;
      const size = props.size || "1em";
      return <span ref={ref} style={{ display: "inline-block", width: size, height: size }} />;
    });
    LazyIcon.displayName = `NavIcon(${kebabToPascal(name)})`;
    lazyIconCache.set(name, LazyIcon);
  }
  return lazyIconCache.get(name);
};

/** Icon component for a module icon name (static alias or any library icon). */
export const resolveNavIcon = (name) => {
  if (!name || typeof name !== "string") return undefined;
  if (Object.prototype.hasOwnProperty.call(staticNavIcons, name)) return staticNavIcons[name];
  if (!ICON_NAME_RE.test(name)) return undefined;
  // Library already here → we know for sure whether the name exists.
  if (getLoadedIconLibrary()) return libraryIcon(name) ? lazyIcon(name) : undefined;
  return lazyIcon(name);
};

// `navIcons[name]` keeps working everywhere it's already used (sidebar,
// navbar, mobile nav, Settings tabs, Module page) — now for every icon.
export const navIcons = new Proxy(staticNavIcons, {
  get: (target, prop) => (typeof prop === "string" ? resolveNavIcon(prop) : undefined),
});
